import { useForm } from '@inertiajs/react'
import { Page, PageHeader } from '@/components/page'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from './Wizard/WizardLayout'
import { WizardActions } from './Wizard/WizardActions'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { useBasePath } from '@/lib/paths'
import { ArrowRight, Home, Globe2 } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'

type CountryOption = { id: string; code: string; name: string }

type Props = {
  prefill: {
    residency: 'us_resident' | 'non_us_resident' | null
    state_code: string | null
    entity_type: string | null
  }
  countries: CountryOption[]
}

// Wizard step 1 (RR-01: this is the formation resource's own new/create —
// no formation exists yet, so it isn't nested under one). Everything after
// this page lives under /formations/:id/wizard/*.
export default function FormationsNew({ prefill, countries }: Props) {
  const basePath = useBasePath()
  const form = useForm({
    founder_residency: prefill.residency ?? '',
    founder_country_id: '',
    state_code: prefill.state_code ?? '',
    entity_type: prefill.entity_type ?? '',
  })

  const isNonUs = form.data.founder_residency === 'non_us_resident'
  const canSubmit = !!form.data.founder_residency && (!isNonUs || !!form.data.founder_country_id)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post(`${basePath}/formations`)
  }

  return (
    <WizardLayout
      title="Where do you live?"
      currentStep="residency"
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'Where do you live?' }]}
      guide={
        <WizardGuide title="Where you live">
          <p>
            Where you live changes your address, your identity paperwork, and how we file your EIN.
            Corppy handles the standard setup either way.
          </p>
        </WizardGuide>
      }
      tips={
        <Tip seed="residency-decides-all">
          Your answer here decides your address, identity paperwork, and each step that follows.
        </Tip>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <Page>
          <PageHeader
            title={<>Where do you live?</>}
            subtitle={<>Where you live changes your address and the identity paperwork we prepare. Corppy handles the standard setup either way.</>}
          />

          <RadioGroup
            value={form.data.founder_residency}
            onValueChange={(value) => form.setData('founder_residency', value)}
            aria-label="Where do you live?"
            aria-invalid={!!form.errors.founder_residency}
            className="grid gap-3 sm:grid-cols-2"
          >
            <label
              htmlFor="residency-us"
              className="group flex items-start gap-3 rounded-lg border p-5 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted hover:border-foreground/40"
            >
              <RadioGroupItem value="us_resident" id="residency-us" className="mt-1" />
              <span className="flex flex-col gap-1.5">
                <span className="flex items-center gap-2 text-base font-medium text-foreground">
                  <Home className="size-4 text-muted-foreground" />
                  I live in the US
                </span>
                <span className="text-sm text-muted-foreground">We'll prepare your standard US setup.</span>
              </span>
            </label>

            <label
              htmlFor="residency-non-us"
              className="group flex items-start gap-3 rounded-lg border p-5 cursor-pointer transition-colors has-data-[state=checked]:border-foreground has-data-[state=checked]:bg-muted hover:border-foreground/40"
            >
              <RadioGroupItem value="non_us_resident" id="residency-non-us" className="mt-1" />
              <span className="flex flex-col gap-1.5">
                <span className="flex items-center gap-2 text-base font-medium text-foreground">
                  <Globe2 className="size-4 text-muted-foreground" />
                  I live outside the US
                </span>
                <span className="text-sm text-muted-foreground">
                  We'll set up your US business address and handle the EIN paperwork.
                </span>
              </span>
            </label>
          </RadioGroup>
          {form.errors.founder_residency && (
            <p className="mt-3 text-sm text-destructive" role="alert">{form.errors.founder_residency}</p>
          )}

          {isNonUs && (
            <div className="mt-5 max-w-sm space-y-1.5">
              <Label htmlFor="founder_country_id">Your country</Label>
              <Combobox
                id="founder_country_id"
                options={countries.map((country) => ({ value: country.id, label: country.name }))}
                value={form.data.founder_country_id}
                onValueChange={(value) => form.setData('founder_country_id', value)}
                placeholder="Choose your country"
                searchPlaceholder="Search countries…"
                emptyMessage="No countries found."
                ariaInvalid={!!form.errors.founder_country_id}
                ariaDescribedBy={form.errors.founder_country_id ? 'founder_country_id-error' : undefined}
              />
              {form.errors.founder_country_id && (
                <p id="founder_country_id-error" className="text-sm text-destructive" role="alert">
                  {form.errors.founder_country_id}
                </p>
              )}
            </div>
          )}

          <WizardActions currentStep="residency" disabled={form.processing}>
            <Button type="submit" disabled={form.processing || !canSubmit}>
              {form.processing ? 'Starting…' : 'Continue'}
              <ArrowRight className="size-4" />
            </Button>
          </WizardActions>
      
        </Page>
      </form>
    </WizardLayout>
  )
}
